import { useCallback } from 'react';

import {
  FALLBACK_SUPPORTED_NATIVE_SCREENS,
  ParamlessWebviewScreenId,
  WebviewScreenId,
  WebviewScreenParams,
} from '@/modules/players/v3/constants/webview-screen-ids';
import {
  getInjectedSupportedScreens,
  isInWebview,
  triggerNativeAction,
} from '@/modules/players/v3/utils/webview-bridge';

type NavigateArgs<T extends WebviewScreenId> = [WebviewScreenParams[T]] extends [never]
  ? [screenId: T]
  : [screenId: T, params: WebviewScreenParams[T]];

export function useWebviewNavigation() {
  const navigateToMobileAppScreen = useCallback(
    <T extends WebviewScreenId>(...args: NavigateArgs<T>) => {
      const [screenId, params] = args;
      triggerNativeAction({
        action: 'navigate',
        screenId,
        ...(params ?? {}),
      });
    },
    []
  );

  const navigateToNativeScreenIfSupported = useCallback(
    (screenId: ParamlessWebviewScreenId): boolean => {
      if (!isInWebview()) {
        return false;
      }
      const supportedScreens = getInjectedSupportedScreens() ?? FALLBACK_SUPPORTED_NATIVE_SCREENS;
      if (!supportedScreens.includes(screenId)) {
        return false;
      }
      triggerNativeAction({ action: 'navigate', screenId });
      return true;
    },
    []
  );

  // Shared click guard for nav links: when the screen is handled natively, stop the web navigation.
  const handleNativeNavClick = useCallback(
    (event: { preventDefault: () => void }, screenId?: ParamlessWebviewScreenId): void => {
      if (screenId && navigateToNativeScreenIfSupported(screenId)) {
        event.preventDefault();
      }
    },
    [navigateToNativeScreenIfSupported]
  );

  return { navigateToMobileAppScreen, navigateToNativeScreenIfSupported, handleNativeNavClick };
}
